// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';
import ModalEditCategory from './ModalEditCategory';
import ModalAddCategory from './ModalAddCategory';
import { 
  fetchMetadataCategoriesHierarchical,
  updateHierarchicalCategory,
  deleteHierarchicalCategory,
  createMetadataCategory
} from '../viewServices/services_hierarchicalCategories';
import { extractL0Name, buildL0Key } from '../../moduleServices/helper_hierarchicalCategory';

// ###### COMPONENT
const CardHierarchicalCategories = () => {
  // ###### STATES
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);

  // ###### EFFECTS
  useEffect(() => {
    fetchCategories();
  }, []);

  // ###### FUNCTIONS
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await fetchMetadataCategoriesHierarchical();
      setCategories(data);
    } catch (error) {
      toast.error('Error loading categories');
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (categoryData) => {
    let l0 = null, l1 = null, l2 = null, type = categoryData.type || 'Out';
    
    if (categoryData.level === 0) {
      l0 = categoryData.name;
      type = null;
    } else if (categoryData.level === 1) {
      const l1Data = categories.by_l1?.[categoryData.id];
      const parentKey = l1Data?.parent || '';
      l0 = extractL0Name(parentKey);
      l1 = l1Data?.name || null;
      type = l1Data?.type || 'Out';
    } else if (categoryData.level === 2) {
      const l2Data = categories.by_l2?.[categoryData.id];
      const parentL0Key = l2Data?.parent_l0 || '';
      l0 = extractL0Name(parentL0Key);
      const l1Data = categories.by_l1?.[l2Data?.parent_l1];
      l1 = l1Data?.name || null;
      l2 = l2Data?.name || null;
      type = l2Data?.type || 'Out';
    }
    
    setEditingCategory({ ...categoryData, l0, l1, l2, type });
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      const { level, id, name, color, is_active } = categoryData;
      
      if (level === 0) {
        const baseL0Name = extractL0Name(id);
        const updatePromises = [];
        
        const inKey = buildL0Key(baseL0Name, 'In');
        if (categories.by_l0?.[inKey]) {
          updatePromises.push(
            updateHierarchicalCategory(level, inKey, 'update', { name, color, is_active })
          );
        }
        
        const outKey = buildL0Key(baseL0Name, 'Out');
        if (categories.by_l0?.[outKey]) {
          updatePromises.push(
            updateHierarchicalCategory(level, outKey, 'update', { name, color, is_active })
          );
        }
        
        await Promise.all(updatePromises);
      } else {
        await updateHierarchicalCategory(level, id, 'update', {
          name,
          color,
          is_active
        });
      }
      
      toast.success('Category updated successfully');
      fetchCategories();
      setEditingCategory(null);
    } catch (error) {
      toast.error('Error updating category');
      console.error('Error updating category:', error);
    }
  };

  const handleDeleteCategory = async (categoryData) => {
    const { level, id, name } = categoryData;
    
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteHierarchicalCategory(level, id);
        toast.success('Category deleted successfully');
        fetchCategories(); // Refresh data
      } catch (error) {
        toast.error('Error deleting category');
        console.error('Error deleting category:', error);
      }
    }
  };

  const handleAddCategory = async ({ l0, l1, l2, type }) => {
    try {
      setAdding(true);
      await createMetadataCategory({ categ_l0: l0, categ_l1: l1, categ_l2: l2, category_type: type });
      toast.success('Category added successfully');
      fetchCategories();
    } catch (error) {
      toast.error('Error adding category');
      console.error('Error adding category:', error);
    } finally {
      setAdding(false);
      setShowAddModal(false);
    }
  };

  // Helper function to render nested blocks, with L2 as horizontal chips inside L1
  const renderNestedTreeWithL2Horizontal = () => {
    if (!categories.by_l0) return null;
    const l0List = Object.entries(categories.by_l0);
    
    // Group by L0 name (without type suffix)
    const groupedByL0Name = {};
    l0List.forEach(([l0Key, l0Data]) => {
      const l0Name = l0Data.name || l0Data.id;
      if (!groupedByL0Name[l0Name]) {
        groupedByL0Name[l0Name] = { In: null, Out: null };
      }
      groupedByL0Name[l0Name][l0Data.type] = { key: l0Key, data: l0Data };
    });
    
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {Object.entries(groupedByL0Name).map(([l0Name, types]) => 
          renderL0Card(l0Name, types)
        )}
      </div>
    );
  };
  
  // Helper to render individual L0 card (with In/Out sections)
  const renderL0Card = (l0Name, types) => {
    const hasIn = types.In !== null;
    const hasOut = types.Out !== null;
    
    // Get first available type data for edit button (prefer In, then Out)
    const editData = hasIn ? types.In : types.Out;
    
    return (
      <div key={l0Name} className="bg-gray-50 border border-gray-200 rounded-xl p-2 md:p-3">
        {/* L0 Header */}
        <div className="flex items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-300">
          <span className="font-semibold text-base">{l0Name}</span>
          <button
            onClick={() => handleEditCategory({
              level: 0,
              id: editData.key,
              name: editData.data.name,
              color: editData.data.color,
              is_active: editData.data.is_active,
              type: editData.data.type
            })}
            className="text-gray-400 hover:text-blue-600 p-1"
            title="Edit"
          >
            <PencilSquareIcon className="h-3 w-3" />
          </button>
        </div>
        
        {/* Income Section */}
        {hasIn && (
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                In
              </span>
            </div>
            {renderL1Children(types.In.data)}
          </div>
        )}
        
        {/* Expense Section */}
        {hasOut && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                Out
              </span>
            </div>
            {renderL1Children(types.Out.data)}
          </div>
        )}
      </div>
    );
  };
  
  // Helper to render L1 children
  const renderL1Children = (l0Data) => {
    if (!l0Data.children || l0Data.children.length === 0) return null;
    
    return (
      <div className="flex flex-col gap-2">
        {l0Data.children.map(l1Id => {
          const l1Data = categories.by_l1?.[l1Id];
          if (!l1Data) return null;
          return (
            <div key={l1Id} className="bg-gray-100 border border-gray-200 rounded-lg p-2">
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={() => handleEditCategory({
                    level: 1,
                    id: l1Id,
                    name: l1Data.name,
                    color: l1Data.color,
                    is_active: l1Data.is_active,
                    type: l1Data.type
                  })}
                  className="text-gray-400 hover:text-blue-600 p-1"
                  title="Edit"
                >
                  <PencilSquareIcon className="h-3 w-3" />
                </button>
                <span className={`font-medium text-sm ${l1Data.is_active === false ? 'opacity-50' : ''}`}>{l1Data.name}</span>
              </div>
              {/* L2 as horizontal chips */}
              {l1Data.children && l1Data.children.length > 0 && (
                <div className="flex flex-wrap gap-2 ml-4 mt-1">
                  {l1Data.children.map(l2Id => {
                    const l2Data = categories.by_l2?.[l2Id];
                    if (!l2Data) return null;
                    return (
                      <div key={l2Id} className="flex items-center gap-1 bg-gray-200 border border-gray-300 rounded-full px-2 py-0.5 w-fit text-xs">
                        <button
                          onClick={() => handleEditCategory({
                            level: 2,
                            id: l2Data.id,
                            name: l2Data.name,
                            color: l2Data.color,
                            is_active: l2Data.is_active,
                            type: l2Data.type
                          })}
                          className="text-gray-400 hover:text-blue-600 p-0.5"
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-3 w-3" />
                        </button>
                        <span className={`font-normal text-sm ${l2Data.is_active === false ? 'opacity-50' : ''}`}>{l2Data.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-white p-3 rounded-2xl shadow flex-1">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">Categories</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1 bg-gray-700 hover:bg-gray-800 text-white px-3 py-1 rounded-lg text-sm font-medium"
        >
          + Add
        </button>
      </div>
      <div className="mt-2">
        {loading ? (
          <div className="flex justify-center py-8">
            <SpinnerLoad />
          </div>
        ) : categories.by_l0 && Object.keys(categories.by_l0).length > 0 ? (
          <div>
            {renderNestedTreeWithL2Horizontal()}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No categories found. Start by creating some categories.
          </div>
        )}
      </div>
      {/* Add Modal */}
      {showAddModal && (
        <ModalAddCategory
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddCategory}
          submitting={adding}
        />
      )}
      {/* Edit Modal */}
      {editingCategory && (
        <ModalEditCategory
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSave={handleSaveCategory}
          onDelete={handleDeleteCategory}
        />
      )}
    </div>
  );
};

export default CardHierarchicalCategories;
